import IrqMainTitle from "../atoms/typography/IrqMainTitle";
import EditActions from "../schema/misc/EditActions";
import { CloseIcon } from "../atoms/icons/close";
import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { getComponentForData } from "../util/getComponentForData";
import { getTitleForDataPointModal } from "../util/getTitleForDataPointModal";
import { getLabelForDataPointModal } from "../util/getLabelForDataPointModal";
import { getOptionsForPointModal } from "../util/getOptionsForPointModal";
import { getMaxAndMinForDataPointModal } from "../util/getMaxAndMinForDataPointModal";
import { object, string } from "yup";

const getValidationSchema = (openDataName, validationObject) => {
  if (
    openDataName === "street" ||
    openDataName === "city" ||
    openDataName === "county" ||
    openDataName === "state" ||
    openDataName === "zip"
  ) {
    return object().shape({
      street: validationObject.street,
      city: validationObject.city,
      county: validationObject.county,
      state: validationObject.state,
      zip: validationObject.zip,
    });
  } else if (openDataName === "firstTimeBuyer") {
    return object().shape({
      firstTimeBuyer: string().required(),
    });
  }
  return object().shape({ [openDataName]: validationObject[openDataName] });
};

export const ModifyDataPointModalContent = React.forwardRef(
  (
    {
      open,
      onClose,
      data,
      onFormEdit,
      customFormEdit,
      openDataName = null,
      isGoogleApiLoaded,
      onGoogleMapLoad,
      onGoogleMapUnmount,
      placesService,
      googleMap,
      setFirstTimeBuyer,
      validationObject,
      onSubmitAddress,
    },
    ref
  ) => {
    const [showMap, setShowMap] = useState(false);
    const [mapHeight, setMapHeight] = useState(0);
    const [center, setCenter] = useState({
      lat: 38.627,
      lng: 90.199,
    });

    const validationSchema = getValidationSchema(
      openDataName,
      validationObject
    );
    const formik = useFormik({
      initialValues: {
        [openDataName]: data[openDataName],
      },
      validationSchema,
    });

    //need this hack because formik doesn't understand setting multiple values at once.
    useEffect(() => {
      formik.validateForm(formik.values);
    }, [
      formik.values.street,
      formik.values.city,
      formik.values.county,
      formik.values.countyFips,
      formik.values.state,
      formik.values.stateFips,
      formik.values.zip,
    ]);

    const firstTimeBuyerSubmit = () => {
      setFirstTimeBuyer(formik.values.firstTimeBuyer);
      onClose();
    };

    const amountOfCashOutSubmit = () => {
      const value = formik.values[openDataName];
      if (value === "0" || !value) {
        customFormEdit("amountOfCashOut", "0");
        customFormEdit("takeCashOut", false);
      } else {
        customFormEdit("amountOfCashOut", value);
        customFormEdit("takeCashOut", true);
      }
      onClose();
    };

    const takeCashOutSubmit = () => {
      const value = formik.values[openDataName];
      if (value === false) {
        customFormEdit("amountOfCashOut", "0");
      } else {
        if (data.amountOfCashOut && data.amountOfCashOut !== "0") {
          customFormEdit("amountOfCashOut", data.amountOfCashOut);
        } else {
          customFormEdit("amountOfCashOut", "1000");
        }
      }
      customFormEdit("takeCashOut", value);
      onClose();
    };

    const standardSubmit = () => {
      customFormEdit(openDataName, formik.values[openDataName]);
      onClose();
    };

    const submitAddress = () => {
      customFormEdit("street", formik.values.street);
      customFormEdit("city", formik.values.city);
      customFormEdit("county", formik.values.county);
      customFormEdit("countyFips", formik.values.countyFips);
      customFormEdit("state", formik.values.state);
      customFormEdit("stateFips", formik.values.stateFips);
      customFormEdit("zip", formik.values.zip);
      customFormEdit("propertyType", formik.values.propertyType);
      customFormEdit("neighborhood", formik.values.neighborhood);

      onSubmitAddress?.({
        street: formik.values.street,
        city: formik.values.city,
        county: formik.values.county,
        countyFips: formik.values.countyFips,
        state: formik.values.state,
        stateFips: formik.values.stateFips,
        zip: formik.values.zip,
        propertyType: formik.values.propertyType,
        neighborhood: formik.values.neighborhood,
      });

      onClose();
    };

    const customSubmit = () => {
      switch (openDataName) {
        case "firstTimeBuyer":
          firstTimeBuyerSubmit();
          break;
        case "amountOfCashOut":
          amountOfCashOutSubmit();
          break;
        case "takeCashOut":
          takeCashOutSubmit();
          break;
        case "city":
        case "street":
        case "state":
        case "county":
        case "zip":
          submitAddress();
          break;
        default:
          standardSubmit();
      }
    };

    const onCustomFormEdit = (name, value) => formik.setFieldValue(name, value);

    const emptyComponent = () => <div />;
    const Component = getComponentForData(openDataName) ?? emptyComponent;
    const title = getTitleForDataPointModal(openDataName);
    const label = getLabelForDataPointModal(openDataName);
    const options = getOptionsForPointModal(openDataName);
    const { max, min } = getMaxAndMinForDataPointModal(openDataName, data);
    const fakeComponent =
      openDataName === "plannedClosingDate"
        ? { props: { customFormEdit: onCustomFormEdit } }
        : undefined;

    const errorText = formik.errors[openDataName];

    return (
      <div ref={ref} className="add-modal">
        <form onSubmit={formik.handleSubmit}>
          <div className="modify-data-point-content-wrapper">
            <div className="irq-title-wrapper">
              <IrqMainTitle>{title}</IrqMainTitle>
            </div>
            <Component
              value={formik.values[openDataName]}
              name={openDataName}
              helperText={errorText}
              error={!!errorText}
              label={label}
              reactComponent={fakeComponent}
              options={options}
              isGoogleApiLoaded={isGoogleApiLoaded}
              showMap={showMap}
              center={center}
              mapHeight={mapHeight}
              onChange={formik.handleChange}
              loanOptions={formik.values[openDataName]}
              onGoogleMapLoad={onGoogleMapLoad}
              onGoogleMapUnmount={onGoogleMapUnmount}
              placesService={placesService}
              customFormEdit={onCustomFormEdit}
              googleMap={googleMap}
              setState={({ showMap, center, mapHeight }) => {
                setShowMap(showMap);
                setCenter(center);
                setMapHeight(mapHeight);
              }}
              setAddress={({
                street,
                city,
                county,
                countyFips,
                state: addressState,
                stateFips,
                zip,
                propertyType,
                neighborhood,
              }) => {
                formik.setFieldValue("street", street);
                formik.setFieldValue("city", city);
                formik.setFieldValue("county", county);
                formik.setFieldValue("countyFips", countyFips);
                formik.setFieldValue("state", addressState);
                formik.setFieldValue("stateFips", stateFips);
                formik.setFieldValue("zip", zip);
                formik.setFieldValue("propertyType", propertyType);
                formik.setFieldValue("neighborhood", neighborhood);
              }}
              inputProps={{ max, min }}
            />
            <EditActions
              canSubmit={formik.isValid}
              label="Submit"
              customSubmit={customSubmit}
              type="submit"
            />
          </div>
          <div className="close-button" onClick={onClose}>
            <CloseIcon />
          </div>
        </form>
      </div>
    );
  }
);
